'use client';

import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import { useCallback } from 'react';
import { cn } from '@/lib/utils';
import { X, SlidersHorizontal } from 'lucide-react';
import type { ExamType } from '@/lib/api';
import { useTranslations } from 'next-intl';

interface CatalogSidebarProps {
  examTypes: ExamType[];
  countLabel: string; // e.g. "modules" | "courses"
  /** Hide the "Jenis Ujian" group — set false when exam type is already surfaced
   *  elsewhere (e.g. the toolbar chip row on /tests) to avoid two controls for the
   *  same filter. Defaults true for existing sidebar-column usage (e.g. /courses). */
  showExamTypeGroup?: boolean;
  /** Sticky-positions the panel within its scroll container. Defaults true for the
   *  sidebar-column layout; pass false when rendered inside a non-sticky dropdown panel. */
  sticky?: boolean;
}

export function CatalogSidebar({ examTypes, countLabel, showExamTypeGroup = true, sticky = true }: CatalogSidebarProps) {
  const t = useTranslations('catalog');
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const activeExamType = searchParams.get('examTypeSlug') ?? '';
  const activeFree = searchParams.get('isFree') ?? '';
  const hasDiscount = searchParams.get('hasDiscount') === 'true';
  const activeDuration = searchParams.get('duration') ?? '';

  const updateFilter = useCallback(
    (key: string, value: string | null) => {
      const params = new URLSearchParams(searchParams.toString());
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      router.replace(`${pathname}?${params.toString()}`, { scroll: false });
    },
    [router, pathname, searchParams]
  );

  const clearAll = () => {
    router.replace(pathname, { scroll: false });
  };

  const hasActiveFilters = activeExamType || activeFree || hasDiscount || activeDuration;

  // Duration buckets differ by product type
  const durationBuckets = countLabel === 'courses'
    ? [
        { label: t('semuaDurasi'), value: '' },
        { label: '< 5 jam', value: 'short' },
        { label: '5–10 jam', value: 'medium' },
        { label: '10+ jam', value: 'long' },
      ]
    : [
        { label: t('semuaDurasi'), value: '' },
        { label: '< 2 jam', value: 'short' },
        { label: '2–5 jam', value: 'medium' },
        { label: '5+ jam', value: 'long' },
      ];

  return (
    <aside className="w-full">
      <div className={cn('bg-white rounded-xl border border-slate-200 p-4', sticky && 'sticky top-[88px]')}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-slate-500" />
            <span className="font-semibold text-slate-900 text-sm">{t('filter')}</span>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearAll}
              className="text-xs text-teal-600 hover:text-teal-700 font-medium flex items-center gap-1"
            >
              <X className="w-3 h-3" />
              {t('reset')}
            </button>
          )}
        </div>

        {/* Tipe: Free / Berbayar */}
        <div className="mb-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('tipe')}</p>
          <div className="flex flex-col gap-1.5">
            {[
              { label: t('semua'), value: '' },
              { label: t('gratis'), value: 'true' },
              { label: t('berbayar'), value: 'false' },
            ].map(({ label, value }) => (
              <button
                key={value}
                onClick={() => updateFilter('isFree', value || null)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                  activeFree === value
                    ? 'bg-teal-50 text-teal-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Penawaran: Sedang Diskon */}
        <div className="mb-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('penawaran')}</p>
          <button
            onClick={() => updateFilter('hasDiscount', hasDiscount ? null : 'true')}
            className={cn(
              'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between',
              hasDiscount
                ? 'bg-teal-50 text-teal-700 font-semibold'
                : 'text-slate-600 hover:bg-slate-50'
            )}
          >
            <span>{t('sedangDiskon')}</span>
            {hasDiscount && <X className="w-3.5 h-3.5 text-teal-500" />}
          </button>
        </div>

        {/* Durasi */}
        <div className="mb-5">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">{t('durasi')}</p>
          <div className="flex flex-col gap-1">
            {durationBuckets.map(({ label, value }) => (
              <button
                key={value}
                onClick={() => updateFilter('duration', value || null)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                  activeDuration === value
                    ? 'bg-teal-50 text-teal-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Jenis Ujian */}
        {showExamTypeGroup && examTypes.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              {t('jenisUjian')}
            </p>
            <div className="flex flex-col gap-1">
              <button
                onClick={() => updateFilter('examTypeSlug', null)}
                className={cn(
                  'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors',
                  !activeExamType
                    ? 'bg-teal-50 text-teal-700 font-semibold'
                    : 'text-slate-600 hover:bg-slate-50'
                )}
              >
                {t('semuaJenis')}
              </button>
              {examTypes.map((et) => {
                const count = countLabel === 'courses' ? et._count.courses : et._count.modules;
                return (
                  <button
                    key={et.slug}
                    onClick={() =>
                      updateFilter('examTypeSlug', activeExamType === et.slug ? null : et.slug)
                    }
                    className={cn(
                      'w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between gap-2',
                      activeExamType === et.slug
                        ? 'bg-teal-50 text-teal-700 font-semibold'
                        : 'text-slate-600 hover:bg-slate-50'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {et.name}
                    </span>
                    <span className="text-xs text-slate-400 shrink-0">{count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}
